import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = import.meta.env.VITE_API_URL || "https://vitalcasa-crm-production.up.railway.app/api";

function authHeader() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useCarla() {
  const [alerts, setAlerts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [chatHistory, setChatHistory] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);

  const loadAlerts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/carla/alerts`, { headers: authHeader() });
      setAlerts(data);
    } catch (e) {
      console.error("[useCarla] alerts:", e.message);
    }
  }, []);

  const loadDocuments = useCallback(async (category = "all") => {
    try {
      const params = category !== "all" ? { category } : {};
      const { data } = await axios.get(`${API}/carla/documents`, { headers: authHeader(), params });
      setDocuments(data);
    } catch (e) {
      console.error("[useCarla] documents:", e.message);
    }
  }, []);

  const loadTickets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/carla/tickets`, { headers: authHeader() });
      setTickets(data);
    } catch (e) {
      console.error("[useCarla] tickets:", e.message);
    }
  }, []);

  const loadChatHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/carla/chat/history`, { headers: authHeader() });
      setChatHistory(data);
    } catch (e) {
      console.error("[useCarla] chatHistory:", e.message);
    }
  }, []);

  const loadContracts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/carla/contracts`, { headers: authHeader() });
      setContracts(data);
    } catch (e) {
      console.error("[useCarla] contracts:", e.message);
    }
  }, []);

  const sendMessage = useCallback(async (message) => {
    setChatLoading(true);
    setChatHistory(prev => [...prev, { role: "user", content: message, created_at: new Date().toISOString() }]);

    const tryRequest = async (attempt) => {
      try {
        const { data } = await axios.post(
          `${API}/carla/chat/message`,
          { message },
          { headers: authHeader(), timeout: 35000 }
        );
        return data;
      } catch (e) {
        const isNetworkError = !e.response && (e.code === 'ERR_NETWORK' || e.message === 'Network Error');
        if (isNetworkError && attempt < 2) {
          await new Promise(r => setTimeout(r, 2000));
          return tryRequest(attempt + 1);
        }
        throw e;
      }
    };

    try {
      const data = await tryRequest(0);
      setChatHistory(prev => [...prev, {
        role: "assistant",
        content: data.reply,
        sources: data.sources,
        suggestions: data.suggestions || [],
        created_at: new Date().toISOString(),
      }]);
      return data;
    } catch (e) {
      const friendlyReply = e.response?.data?.reply;
      const errMsg = e.response?.data?.error || e.message;
      setChatHistory(prev => [...prev, {
        role: "assistant",
        content: friendlyReply || `❌ Error: ${errMsg}`,
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setChatLoading(false);
    }
  }, []);

  const clearChat = useCallback(async () => {
    try {
      await axios.delete(`${API}/carla/chat/history`, { headers: authHeader() });
      setChatHistory([]);
    } catch (e) {
      console.error("[useCarla] clearChat:", e.message);
    }
  }, []);

  const createTicket = useCallback(async (subject, category, priority) => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/carla/tickets`,
        { subject, category, priority },
        { headers: authHeader() }
      );
      setTickets(prev => [data, ...prev]);
      return data;
    } catch (e) {
      throw new Error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const generateContract = useCallback(async (params) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/carla/contracts/generate`, params, { headers: authHeader() });
      await loadContracts();
      return data;
    } catch (e) {
      throw new Error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [loadContracts]);

  const runSeed = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/carla/admin/seed`, {}, { headers: authHeader() });
      if (data.ok) {
        await loadDocuments();
        await loadAlerts();
      }
      return data;
    } catch (e) {
      return { ok: false, msg: e.response?.data?.error || e.message };
    }
  }, [loadDocuments, loadAlerts]);

  const runSearchNow = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/carla/admin/search-now`, {}, { headers: authHeader() });
      return data;
    } catch (e) {
      return { ok: false, msg: e.response?.data?.error || e.message };
    }
  }, []);

  const getStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/carla/admin/status`, { headers: authHeader() });
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  const runAutoAudit = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/carla/admin/auto-audit`, {}, { headers: authHeader() });
      return data;
    } catch (e) {
      return { ok: false, msg: e.response?.data?.error || e.message };
    }
  }, []);

  const runAudit = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/carla/admin/audit`, { headers: authHeader() });
      return data;
    } catch (e) {
      return { ok: false, audit: null, error: e.response?.data?.error || e.message };
    }
  }, []);

  const uploadPdf = useCallback(async (file, meta) => {
    const formData = new FormData();
    formData.append('pdf', file);
    formData.append('title', meta.title);
    formData.append('category', meta.category || 'ley');
    formData.append('source', meta.source || 'Subido por usuario');
    formData.append('lang', meta.lang || 'ES');
    if (meta.doc_date) formData.append('doc_date', meta.doc_date);
    if (meta.is_own) formData.append('is_own', 'true');
    try {
      const { data } = await axios.post(`${API}/carla/documents/upload`, formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      await loadDocuments();
      return data;
    } catch (e) {
      throw new Error(e.response?.data?.error || e.message);
    }
  }, [loadDocuments]);

  useEffect(() => {
    loadAlerts();
    loadDocuments();
    loadTickets();
    loadChatHistory();
    loadContracts();
  }, [loadAlerts, loadDocuments, loadTickets, loadChatHistory, loadContracts]);

  return {
    alerts, documents, tickets, chatHistory, contracts,
    loading, chatLoading,
    sendMessage, clearChat, createTicket, generateContract,
    loadAlerts, loadDocuments, loadTickets, loadContracts,
    runSeed, runSearchNow, getStatus, runAudit, runAutoAudit, uploadPdf,
  };
}
